import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  getCurrentUserId,
  getDealApplicationId,
  getEncompassLoanId,
  getLoanId,
  getMyLoansFetchedAt,
  getMyLoansStatus,
  getNhDealApplicationId,
} from "features/authSlice";
import { useGetMyLoansQuery } from "pages/application/applicationService";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { ENCOMPASS_LOS_TYPE, MY_LOANS_SIDEBAR_TTL_MS } from "utilities/loan-sidebar/constants";
import { filterEncompassLoans } from "utilities/loan-sidebar/filterEncompassLoans";
import { getLoanKey } from "utilities/loan-sidebar/getLoanKey";
import { getLoanSidebarLabel } from "utilities/loan-sidebar/getLoanSidebarLabel";
import { isActiveLoanMatch } from "utilities/loan-sidebar/isActiveLoanMatch";
import { mapLoanStatusToBaseState } from "utilities/loan-sidebar/mapLoanStatusToBaseState";
import { sortSidebarLoans } from "utilities/loan-sidebar/sortSidebarLoans";
import { LoanSidebarLoanRow, LoanSidebarSessionLoan } from "utilities/loan-sidebar/types";

export interface UseLoanSidebarDataResult {
  readonly loans: LoanSidebarLoanRow[];
  readonly sessionLoan: LoanSidebarSessionLoan;
  readonly isLoading: boolean;
  readonly portfolioLoanCount: number;
}

export const useLoanSidebarData = (): UseLoanSidebarDataResult => {
  const userId = useSelector(getCurrentUserId);
  const myLoansFetchedAt = useSelector(getMyLoansFetchedAt);
  const myLoansStatus = useSelector(getMyLoansStatus);
  const encompassLoanId = useSelector(getEncompassLoanId);
  const loanId = useSelector(getLoanId);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const dealApplicationId = useSelector(getDealApplicationId);
  const { data: featureFlags } = useGetFeatureFlagsQuery();

  const dualLosPortfolioEnabled = Boolean(
    (featureFlags as { dualLosPortfolioEnabled?: boolean } | undefined)?.dualLosPortfolioEnabled,
  );

  const isStale = !myLoansFetchedAt || Date.now() - myLoansFetchedAt > MY_LOANS_SIDEBAR_TTL_MS;
  const shouldFetchFromQuery =
    Boolean(userId) && (myLoansStatus === "idle" || myLoansStatus === "rejected" || isStale);

  const { data, isLoading, isFetching } = useGetMyLoansQuery(userId, {
    skip: !userId,
    refetchOnMountOrArgChange: shouldFetchFromQuery,
  });

  const sessionLoan = useMemo<LoanSidebarSessionLoan>(
    () => ({
      losType: ENCOMPASS_LOS_TYPE,
      loanKey: encompassLoanId ?? loanId ?? nhDealApplicationId ?? dealApplicationId ?? "",
    }),
    [dealApplicationId, encompassLoanId, loanId, nhDealApplicationId],
  );

  const loans = useMemo(() => {
    const encompassLoans = sortSidebarLoans(filterEncompassLoans(data, dualLosPortfolioEnabled));

    return encompassLoans.map((loan) => {
      const rowLoan: LoanSidebarSessionLoan = {
        losType: ENCOMPASS_LOS_TYPE,
        loanKey: getLoanKey(loan),
      };

      return {
        id: loan.nhDealApplicationId,
        losType: ENCOMPASS_LOS_TYPE,
        loanKey: rowLoan.loanKey,
        label: getLoanSidebarLabel(loan),
        baseState: mapLoanStatusToBaseState(loan.status),
        isActive: isActiveLoanMatch(sessionLoan, rowLoan),
        loan,
      };
    });
  }, [data, dualLosPortfolioEnabled, sessionLoan]);

  return {
    loans,
    sessionLoan,
    isLoading: (isLoading || isFetching) && !data?.length,
    portfolioLoanCount: data?.length ?? 0,
  };
};
